import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { apiPost } from "../../shared/apiClient";
import { deleteDepartment, fetchDepartmentsList } from "../../shared/organizationMastersApi";
import { FormModalShell } from "../../shared/FormModalShell";
import { IconClose, IconPlus, IconSave } from "../../shared/toolbarIcons";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

const emptyForm = () => ({
  name: "",
  isActive: true
});

export function DepartmentsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [formError, setFormError] = useState(null);

  const rows = useMemo(
    () => items.map((d) => ({ id: d.id, department: d })),
    [items]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchDepartmentsList();
    if (!res.ok) {
      setError(res.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(res.items);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this department?"))) return;
      const res = await deleteDepartment(id);
      if (!res.ok) setError(res.error ?? "Failed to delete department");
      else {
        if (editingId === id) {
          setIsModalOpen(false);
          setEditingId(null);
          setForm(emptyForm());
          setFormError(null);
        }
        await load();
      }
    },
    [editingId, load]
  );

  const openCreateModal = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((department) => {
    setEditingId(department.id);
    setForm({
      name: department.name ?? "",
      isActive: Boolean(department.isActive)
    });
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError("Department name is required.");
      return;
    }

    setSaving(true);
    setFormError(null);

    const res = await apiPost("/api/Departments", {
      id: editingId ?? 0,
      name: form.name.trim(),
      isActive: form.isActive
    });

    if (!res.ok) {
      setFormError(res.error ?? "Failed to save department");
      setSaving(false);
      return;
    }

    closeModal();
    await load();
    setSaving(false);
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 160,
        valueGetter: (_v, row) => row.department.name ?? "-"
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const d = params.row.department;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="Edit" onClick={() => openEditModal(d)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(d.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [handleDeleteClick, openEditModal]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Departments</h2>
          <p>{loading ? "Loading departments..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <button className="icon-button icon-button--toolbar" type="button" title="Add department" aria-label="Add department" onClick={openCreateModal}>
            <IconPlus />
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <FormModalShell
        open={isModalOpen}
        title={editingId ? "Edit Department" : "Add Department"}
        subtitle="Manage department details."
        onClose={closeModal}
        actions={
          <>
            <button
              type="button"
              className="icon-button icon-button--toolbar"
              onClick={closeModal}
              disabled={saving}
              title="Cancel"
              aria-label="Cancel"
            >
              <IconClose />
            </button>
            <button
              type="submit"
              form="department-modal-form"
              className="icon-button icon-button--toolbar"
              disabled={saving}
              title={saving ? "Saving" : "Save"}
              aria-label={saving ? "Saving" : "Save"}
            >
              <IconSave />
            </button>
          </>
        }
      >
        <form id="department-modal-form" onSubmit={handleSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--4">
            <label className="field">
              <span>Department name</span>
              <input type="text" value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </label>
          </div>
          {formError && <div className="error-banner">{formError}</div>}
        </form>
      </FormModalShell>
    </div>
  );
}
